import React, { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Mail,
  Video as VideoIcon,
} from 'lucide-react';
import LogoWhite from '../../../assets/images/logos/white-bg-logo-hiacuity.png';
import { toast } from '../../../ui/toast';
import { useGetPublicCCAConfigQuery } from '../../../utils/api_service/ccaApi';
import {
  useGetPreQualFormQuery,
  useGetPreQualVideoPresignMutation,
  useGetPublicPreQualVideoPresignMutation,
  useSubmitPreQualMutation,
  useSubmitPublicPreQualMutation,
} from '../../../utils/api_service/preQualApi';
import { awsUploadVideo } from '../../../utils/api_service/storage_api';
import { LocationPicker } from '../viewJobPosting/JobPostDetails/components/LocationPicker';

type VideoAnswerValue = {
  file: File;
  previewUrl: string;
};

type VideoQuestionInputProps = {
  value?: VideoAnswerValue;
  onChange: (value: VideoAnswerValue) => void;
  disabled?: boolean;
};

type RecordingMode = 'idle' | 'camera' | 'recording' | 'processing';

const isVideoQuestionType = (questionType?: string) =>
  questionType === 'video-answer' || questionType === 'video';

const getApiErrorMessage = (error: any) => {
  const detail = error?.data?.detail || error?.data?.message || error?.error;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => item?.msg || item?.message || JSON.stringify(item))
      .join(', ');
  }
  return typeof detail === 'string' ? detail : '';
};

const VideoQuestionInput: React.FC<VideoQuestionInputProps> = ({
  value,
  onChange,
  disabled,
}) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const chunksRef = React.useRef<Blob[]>([]);
  const mediaRecorderRef = React.useRef<MediaRecorder | null>(null);
  const timerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const [mode, setMode] = React.useState<RecordingMode>('idle');
  const [cameraError, setCameraError] = React.useState<string | null>(null);
  const [recordingSecs, setRecordingSecs] = React.useState(0);

  // Recording timer — starts/stops with mode
  React.useEffect(() => {
    if (mode === 'recording') {
      setRecordingSecs(0);
      timerRef.current = setInterval(() => setRecordingSecs((s) => s + 1), 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [mode]);

  const formatRecordingTime = (secs: number) => {
    const m = Math.floor(secs / 60)
      .toString()
      .padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // Stop and release the live camera stream
  const stopStream = React.useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      stopStream();
    };
  }, [stopStream]);

  // Attach stream to video element whenever mode switches to camera/recording
  React.useEffect(() => {
    if (
      (mode === 'camera' || mode === 'recording') &&
      videoRef.current &&
      streamRef.current
    ) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.muted = true;
      videoRef.current.play().catch(() => {});
    }
  }, [mode]);

  const openCamera = async () => {
    try {
      setCameraError(null);
      if (!streamRef.current) {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true,
        });
        streamRef.current = stream;
      }
      setMode('camera');
    } catch {
      setCameraError(
        'Camera access failed. You can upload a recorded video instead.',
      );
    }
  };

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const rec = new MediaRecorder(streamRef.current);
    mediaRecorderRef.current = rec;
    rec.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: 'video/webm' });
      const file = new File([blob], `prequal-${Date.now()}.webm`, {
        type: 'video/webm',
      });
      stopStream();
      onChange({ file, previewUrl: URL.createObjectURL(blob) });
      setMode('idle');
    };
    rec.start();
    setMode('recording');
  };

  const stopRecording = () => {
    setMode('processing');
    mediaRecorderRef.current?.stop();
    mediaRecorderRef.current = null;
  };

  const handleRecordAgain = () => {
    stopStream();
    setMode('idle');
    openCamera();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    stopStream();
    setMode('idle');
    onChange({ file, previewUrl: URL.createObjectURL(file) });
  };

  const showPlayback = !!value?.previewUrl && mode === 'idle';
  const showLiveCamera = mode === 'camera' || mode === 'recording';

  return (
    <Stack spacing={2}>
      {/* Video Viewport */}
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          bgcolor: '#020617',
          overflow: 'hidden',
          aspectRatio: '16/9',
          maxWidth: 440,
        }}
      >
        {/* Playback */}
        {showPlayback && (
          <Box
            component="video"
            key={value!.previewUrl}
            src={value!.previewUrl}
            controls
            sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        )}

        {/* Live Camera */}
        {showLiveCamera && (
          <Box
            component="video"
            ref={videoRef}
            autoPlay
            playsInline
            muted
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}

        {/* Processing Spinner */}
        {mode === 'processing' && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              bgcolor: '#020617',
            }}
          >
            <CircularProgress size={32} sx={{ color: 'common.white' }} />
            <Typography variant="caption" sx={{ color: 'grey.400' }}>
              Processing recording…
            </Typography>
          </Box>
        )}

        {/* Idle Placeholder */}
        {mode === 'idle' && !value?.previewUrl && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              color: 'grey.500',
            }}
          >
            <VideoIcon size={36} opacity={0.4} />
            <Typography variant="caption" color="text.secondary">
              Record or upload a video response
            </Typography>
          </Box>
        )}

        {/* Recording Badge */}
        {mode === 'recording' && (
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              left: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'rgba(220, 38, 38, 0.9)',
              color: 'common.white',
              px: 1.25,
              py: 0.5,
              borderRadius: 5,
              boxShadow: 2,
            }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: 'common.white',
              }}
            />
            <Typography variant="caption" fontWeight={700}>
              REC
            </Typography>
            <Typography
              variant="caption"
              sx={{ fontFamily: 'monospace', letterSpacing: 1 }}
            >
              {formatRecordingTime(recordingSecs)}
            </Typography>
          </Box>
        )}
      </Paper>

      {cameraError && (
        <Alert severity="warning" sx={{ maxWidth: 440 }}>
          {cameraError}
        </Alert>
      )}

      {/* Controls */}
      <Stack direction="row" spacing={1} flexWrap="wrap">
        {showPlayback ? (
          <>
            <Button
              variant="contained"
              color="success"
              size="small"
              disabled={disabled}
              onClick={handleRecordAgain}
            >
              Record Again
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={disabled}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload Instead
            </Button>
          </>
        ) : mode === 'camera' ? (
          <Button
            variant="contained"
            color="error"
            size="small"
            disabled={disabled}
            onClick={startRecording}
          >
            Start Recording
          </Button>
        ) : mode === 'recording' ? (
          <Button
            variant="contained"
            color="error"
            size="small"
            disabled={disabled}
            onClick={stopRecording}
          >
            Stop Recording
          </Button>
        ) : mode === 'processing' ? (
          <Button variant="contained" disabled size="small">
            Processing…
          </Button>
        ) : (
          <>
            <Button
              variant="contained"
              color="success"
              size="small"
              disabled={disabled}
              onClick={openCamera}
            >
              Record Video
            </Button>
            <Button
              variant="outlined"
              size="small"
              disabled={disabled}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload Video
            </Button>
          </>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="video/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </Stack>
    </Stack>
  );
};

export const PreQualForm: React.FC = () => {
  const { jobOpeningId: pJobOpeningId } =
    useParams<{ jobOpeningId?: string }>();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get('token') || '';

  const getTokenPayload = (t: string) => {
    try {
      const base64Url = t.split('.')[1];
      if (!base64Url) return null;
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      return JSON.parse(window.atob(base64));
    } catch {
      return null;
    }
  };

  const tokenPayload = getTokenPayload(token);
  const jobApplicantId =
    queryParams.get('jobApplicantId') ||
    queryParams.get('job_applicant_id') ||
    tokenPayload?.job_applicant_id ||
    tokenPayload?.jobApplicantId ||
    tokenPayload?.applicant_id ||
    '';

  const getJobOpeningIdFromToken = () => {
    try {
      return tokenPayload?.job_opening_id || tokenPayload?.jobOpeningId || '';
    } catch {
      return '';
    }
  };

  const jobOpeningId =
    pJobOpeningId ||
    queryParams.get('jobOpeningId') ||
    getJobOpeningIdFromToken();

  const { data, isLoading, error } = useGetPreQualFormQuery(jobOpeningId, {
    skip: !jobOpeningId,
  });

  const { data: ccaConfig } = useGetPublicCCAConfigQuery(
    { jobOpeningId, token },
    {
      skip: !jobOpeningId || !token,
    },
  );
  const ccaEnabled =
    !!ccaConfig &&
    ((ccaConfig.questions?.length ?? 0) > 0 ||
      (ccaConfig.quickConfig?.subdomains?.length ?? 0) > 0);

  const [submitPreQual, { isLoading: isSubmitting }] =
    useSubmitPreQualMutation();
  const [submitPublicPreQual, { isLoading: isSubmittingPublic }] =
    useSubmitPublicPreQualMutation();
  const [getVideoPresign] = useGetPreQualVideoPresignMutation();
  const [getPublicVideoPresign] = useGetPublicPreQualVideoPresignMutation();
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [locationDataMap, setLocationDataMap] = useState<Record<string, any>>(
    {},
  );
  const [isSubmitted, setIsSubmitted] = useState(false);

  const uploadVideoAndGetKey = async (questionKey: string, file: File) => {
    if (!jobApplicantId) {
      throw new Error('Missing job applicant id');
    }

    const presign = token
      ? await getVideoPresign({ questionKey, jobApplicantId, token }).unwrap()
      : await getPublicVideoPresign({
        questionKey,
        jobApplicantId,
      }).unwrap();

    if (!presign.url) {
      throw new Error('Missing upload URL');
    }

    const typelessBlob = new Blob([file]);
    const uploadResponse = await awsUploadVideo(presign.url, typelessBlob);
    if (!uploadResponse.ok) {
      throw new Error('Failed to upload video response');
    }

    return presign.videoKey || presign.url;
  };

  const handleInputChange = (
    questionKey: string,
    value: any,
    locData?: any,
  ) => {
    setAnswers((prev) => ({
      ...prev,
      [questionKey]: value,
    }));
    if (locData) {
      setLocationDataMap((prev) => ({
        ...prev,
        [questionKey]: locData,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token && !jobApplicantId) return;

    try {
      const missingAnswer = data?.questions.find((question) => {
        const value = answers[question.questionKey || ''];
        return value === undefined || value === null || value === '';
      });

      if (missingAnswer) {
        toast.error('Please answer all questions before submitting.');
        return;
      }

      const formattedAnswers = await Promise.all(
        Object.entries(answers).map(async ([key, value]) => {
          const loc = locationDataMap[key];
          const question = data?.questions.find((q) => q.questionKey === key);
          const isVideoQuestion = isVideoQuestionType(question?.questionType);
          const baseAnswer = {
            questionKey: key,
            value: isVideoQuestion ? '' : String(value),
          };

          if (isVideoQuestion) {
            const videoKey = await uploadVideoAndGetKey(key, value.file);
            return {
              ...baseAnswer,
              value: videoKey,
              videoKey,
            };
          }

          if (question?.questionType === 'location') {
            return {
              ...baseAnswer,
              location: loc
                ? {
                  city: loc.city,
                  state: loc.state,
                  country: loc.country,
                  postcode: loc.postcode,
                  lat: loc.coordinates.coordinates[1],
                  lng: loc.coordinates.coordinates[0],
                  unknown: false,
                }
                : {
                  city: '',
                  state: '',
                  country: '',
                  postcode: '',
                  lat: 0,
                  lng: 0,
                  unknown: true,
                },
            };
          }

          return baseAnswer;
        }),
      );

      if (token) {
        await submitPreQual({ token, answers: formattedAnswers }).unwrap();
      } else {
        await submitPublicPreQual({
          jobApplicantId,
          answers: formattedAnswers,
        }).unwrap();
      }
      setIsSubmitted(true);
      toast.success('Questionnaire submitted successfully!');
    } catch (err) {
      const message = getApiErrorMessage(err);
      toast.error(
        message || 'Failed to submit questionnaire. Please try again.',
      );
    }
  };

  if (!token && !jobApplicantId) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
          p: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            textAlign: 'center',
            maxWidth: 420,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'error.50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <AlertCircle size={28} color="#dc2626" />
          </Box>
          <Typography variant="h6" fontWeight={700} color="text.primary" mb={1}>
            Invalid Link
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This link seems to be invalid or expired. Please check your email or
            contact support.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress color="success" size={48} />
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            Loading questionnaire...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
          p: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            textAlign: 'center',
            maxWidth: 420,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'warning.50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <AlertCircle size={28} color="#d97706" />
          </Box>
          <Typography variant="h6" fontWeight={700} color="text.primary" mb={1}>
            Error Loading
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We couldn&apos;t load your questionnaire. The link might have expired.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (isSubmitted) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
          p: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            textAlign: 'center',
            maxWidth: 460,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'success.50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <CheckCircle2 size={32} color="#16a34a" />
          </Box>
          <Typography variant="h5" fontWeight={700} color="text.primary" mb={1}>
            Application Received
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Thank you for completing the questionnaire for the{' '}
            <Typography component="span" fontWeight={700} color="success.main">
              {data.jobTitle}
            </Typography>{' '}
            position at{' '}
            <Typography component="span" fontWeight={700} color="success.main">
              {data.orgName}
            </Typography>
            .
          </Typography>

          {ccaEnabled && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mt: 3,
                bgcolor: 'info.50',
                border: 1,
                borderColor: 'info.200',
                borderRadius: 2,
                textAlign: 'left',
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    bgcolor: 'info.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Mail size={12} color="#ffffff" />
                </Box>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  color="info.dark"
                  textTransform="uppercase"
                >
                  Next Step: Cognitive Assessment
                </Typography>
              </Stack>
              <Typography variant="caption" color="info.dark">
                You will shortly receive an email with a link to a{' '}
                <Typography component="span" fontWeight={700}>
                  Cognitive Assessment
                </Typography>
                . Please check your inbox (and spam/junk folder) and complete the
                assessment to finalise your application.
              </Typography>
            </Paper>
          )}

          <Divider sx={{ my: 3 }} />
          <Typography variant="caption" color="text.disabled">
            Our hiring team will review your responses and get back to you soon.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Header */}
      <Paper
        elevation={0}
        square
        sx={{
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 30,
        }}
      >
        <Container maxWidth="md" sx={{ py: 1.5 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                component="img"
                src={LogoWhite}
                alt="Logo"
                sx={{ height: 32, width: 32, objectFit: 'contain' }}
                onError={(e: any) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <Divider orientation="vertical" flexItem sx={{ height: 24, my: 'auto' }} />
              <Box>
                <Typography
                  variant="caption"
                  fontWeight={800}
                  color="success.main"
                  textTransform="uppercase"
                  display="block"
                  lineHeight={1}
                  mb={0.25}
                >
                  Candidate Portal
                </Typography>
                <Typography variant="subtitle2" fontWeight={700} lineHeight={1}>
                  {data.orgName}
                </Typography>
              </Box>
            </Stack>

            <Box sx={{ textAlign: 'right' }}>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                textTransform="uppercase"
                display="block"
              >
                Position
              </Typography>
              <Typography variant="caption" fontWeight={700} color="text.primary">
                {data.jobTitle}
              </Typography>
            </Box>
          </Stack>
        </Container>
      </Paper>

      <Container maxWidth="sm" sx={{ py: 5 }}>
        {/* Intro */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={800} color="text.primary" mb={1}>
            Pre-Qualification Form
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please answer the questions below to continue your application for the{' '}
            <Typography component="span" fontWeight={700} color="text.primary">
              {data.jobTitle}
            </Typography>{' '}
            role.
          </Typography>

          {ccaEnabled && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mt: 2.5,
                bgcolor: 'info.50',
                border: 1,
                borderColor: 'info.200',
                borderRadius: 2,
              }}
            >
              <Typography variant="caption" color="info.dark">
                <Typography component="span" fontWeight={700}>
                  Heads up:
                </Typography>{' '}
                After submitting this form you will receive an email with a{' '}
                <Typography component="span" fontWeight={600} fontStyle="italic">
                  Cognitive Assessment
                </Typography>{' '}
                link. Check your inbox (and spam folder) and complete it to
                finalise your application.
              </Typography>
            </Paper>
          )}
        </Box>

        {/* Form */}
        <Box component="form" onSubmit={handleSubmit}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: 1,
              borderColor: 'divider',
              overflow: 'hidden',
            }}
          >
            <Stack divider={<Divider />}>
              {data.questions.map((q, index) => (
                <Box
                  key={q.questionKey || index}
                  sx={{
                    p: 3,
                    transition: 'background-color 0.2s',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start" mb={2}>
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 1,
                        bgcolor: 'success.main',
                        color: 'common.white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '0.8rem',
                        flexShrink: 0,
                        mt: 0.25,
                      }}
                    >
                      {index + 1}
                    </Box>
                    <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                      {q.question}
                    </Typography>
                  </Stack>

                  <Box sx={{ pl: 5 }}>
                    {q.questionType === 'number' && (
                      <TextField
                        type="number"
                        required
                        size="small"
                        placeholder="Answer..."
                        value={answers[q.questionKey!] || ''}
                        inputProps={{ min: 0 }}
                        onKeyDown={(e) => {
                          if (e.key === '-' || e.key === 'e') {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val !== '' && Number(val) < 0) return;
                          handleInputChange(q.questionKey!, val);
                        }}
                        sx={{ maxWidth: 220 }}
                      />
                    )}

                    {q.questionType === 'multiple-choice' && (
                      <FormControl component="fieldset" fullWidth>
                        <RadioGroup
                          name={q.questionKey}
                          value={answers[q.questionKey!] || ''}
                          onChange={(e) =>
                            handleInputChange(q.questionKey!, e.target.value)
                          }
                        >
                          <Grid container spacing={1.5}>
                            {q.choices.map((choice) => (
                              <Grid item xs={12} sm={6} key={choice}>
                                <Paper
                                  elevation={0}
                                  sx={{
                                    px: 2,
                                    py: 0.5,
                                    border: 1,
                                    borderColor:
                                      answers[q.questionKey!] === choice
                                        ? 'success.main'
                                        : 'divider',
                                    bgcolor:
                                      answers[q.questionKey!] === choice
                                        ? 'success.50'
                                        : 'background.paper',
                                    borderRadius: 2,
                                    transition: 'all 0.2s',
                                  }}
                                >
                                  <FormControlLabel
                                    value={choice}
                                    control={<Radio color="success" size="small" />}
                                    label={
                                      <Typography variant="body2" fontWeight={600}>
                                        {choice}
                                      </Typography>
                                    }
                                    sx={{ width: '100%', m: 0 }}
                                  />
                                </Paper>
                              </Grid>
                            ))}
                          </Grid>
                        </RadioGroup>
                      </FormControl>
                    )}

                    {q.questionType === 'text' && (
                      <TextField
                        required
                        multiline
                        rows={4}
                        fullWidth
                        placeholder="Type your answer..."
                        value={answers[q.questionKey!] || ''}
                        onChange={(e) =>
                          handleInputChange(q.questionKey!, e.target.value)
                        }
                      />
                    )}

                    {q.questionType === 'location' && (
                      <Box sx={{ width: '100%' }}>
                        <LocationPicker
                          value={answers[q.questionKey!] || ''}
                          onChange={(val, locData) =>
                            handleInputChange(q.questionKey!, val, locData)
                          }
                        />
                      </Box>
                    )}

                    {isVideoQuestionType(q.questionType) && (
                      <VideoQuestionInput
                        value={answers[q.questionKey!] as VideoAnswerValue}
                        disabled={isSubmitting || isSubmittingPublic}
                        onChange={(val) =>
                          handleInputChange(q.questionKey!, val)
                        }
                      />
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>
          </Paper>

          {/* Submit Section */}
          <Box sx={{ pt: 3 }}>
            <Button
              type="submit"
              variant="contained"
              color="success"
              fullWidth
              size="large"
              disabled={isSubmitting || isSubmittingPublic}
              endIcon={
                isSubmitting || isSubmittingPublic ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <ArrowRight size={18} />
                )
              }
              sx={{
                py: 1.5,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 1,
                borderRadius: 2,
              }}
            >
              {isSubmitting || isSubmittingPublic
                ? 'Submitting...'
                : 'Submit Answers'}
            </Button>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={700}
              textTransform="uppercase"
              display="block"
              textAlign="center"
              mt={2}
            >
              Progress: {Object.keys(answers).length} of {data.questions.length}{' '}
              answered
            </Typography>
          </Box>
        </Box>
      </Container>

      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography
          variant="caption"
          color="text.disabled"
          fontWeight={700}
          textTransform="uppercase"
          letterSpacing={1}
        >
          Powered by HiAcuity Intelligence
        </Typography>
      </Box>
    </Box>
  );
};
